import { useEffect } from 'react';

import { useHasPermissions } from '~domains/auth';
import type { FindPaymentsRequestParams } from '~domains/transactions/entities/payment';
import { useGenerateReportMutation } from '~domains/transactions/entities/payment';
import { isReportGenerated } from '~domains/transactions/entities/payment/lib/is-report-generated';
import {
  useCheckReportStatusQuery,
  useDownloadReportQuery,
} from '~domains/transactions/entities/report';

import { ampli } from '~shared/analytics';
import DownloadIcon from '~shared/assets/icons/download.svg?react';

import { Button } from '@breezeventures/ui-kit/button';

export const PaymentsExport = ({
  params,
  disabled,
  size = 'm',
}: {
  params?: FindPaymentsRequestParams;
  disabled?: boolean;
  size?: 's' | 'm';
}) => {
  const { checkPermissions } = useHasPermissions();
  const canExport = checkPermissions(['TRANSACTIONS_EXPORT']);
  const generateReportMutation = useGenerateReportMutation();
  const checkStatusQuery = useCheckReportStatusQuery(
    generateReportMutation.data?.reportId
  );
  const downloadQuery = useDownloadReportQuery({
    reportData: checkStatusQuery.data,
    screen: 'payments',
  });

  useEffect(() => {
    if (downloadQuery.isSuccess) {
      generateReportMutation.reset();
    }
  }, [downloadQuery.isSuccess, generateReportMutation]);

  if (!canExport) {
    return null;
  }

  const handleExportClick = () => {
    if (!params) return;
    generateReportMutation.mutate(params);
    ampli.paymentsScreenTapExport();
  };
  const reportGenerationInProgress =
    generateReportMutation.isSuccess &&
    !isReportGenerated(checkStatusQuery.data);
  const downloadInProgress =
    isReportGenerated(checkStatusQuery.data) && downloadQuery.isLoading;

  const showLoader =
    generateReportMutation.isPending ||
    reportGenerationInProgress ||
    downloadInProgress;

  return (
    <Button
      type="button"
      size={size}
      variant="secondary"
      loading={showLoader}
      disabled={showLoader || disabled || !params}
      onClick={handleExportClick}
    >
      {!showLoader && (
        <Button.Icon>
          <DownloadIcon />
        </Button.Icon>
      )}
      Export to Excel
    </Button>
  );
};
